import React, { useState, useEffect } from 'react';
import SEO from '../components/SEO';
import Footer from '../components/Footer';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  User,
  Shield,
  Award,
  AlertTriangle,
  BookOpen,
  Clock,
  CreditCard,
  ChevronDown,
} from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { isSupabaseConfigured, supabase, supabaseConfigError, type RegistrationData, type CourseData } from '../lib/supabase';
import { fetchWebsiteCourses } from '../lib/websiteCourses';
import { inputClass } from '../constants/classNames';

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  gender: string;
  age: string;
  instagram: string;
  currentLevel: string;
  selectedCourse: string;
  paymentMethod: string;
  privacy: boolean;
  terms: boolean;
  withdrawalConsent: boolean;
  newsletter: boolean;
}

const labelClass = 'block text-[13px] font-semibold text-gray-600 mb-1.5 tracking-wide';

const FORM_STORAGE_KEY = 'arabicroots_form';

const defaultFormData: FormData = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  gender: '',
  age: '',
  instagram: '',
  currentLevel: '',
  selectedCourse: '',
  paymentMethod: '',
  privacy: false,
  terms: false,
  withdrawalConsent: false,
  newsletter: false,
};

const RegistrationPage = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchParams] = useSearchParams();
  const lockedCourseFromUrl = !!searchParams.get('course');
  const totalSteps = 2;

  const fallbackCourses: CourseData[] = [
    {
      id: 'level-1', level: 'Level 1', title: 'Buchstaben & Tajweedkurs',
      description: 'Perfekt für absolute Anfänger. Lerne das arabische Alphabet von Grund auf.',
      duration: 'ca. 20 Wochen', sessions: '2x die Woche (je ca. 60min)',
      price_monthly: 50, price_full: 300, is_active: true, color: '',
      skills: ['Alle Buchstaben lesen und schreiben', 'Lese & Schreibregeln', 'Basic Tajweed-Regeln', 'Korrekte Aussprache'],
    },
    {
      id: 'level-2', level: 'Level 2', title: 'Madina Buch 1 & Arabya Bayna Yadayk 1',
      description: 'Für alle, die das Alphabet bereits beherrschen und die ersten Grundlagen lernen möchten.',
      duration: 'ca. 34 Wochen', sessions: '2x die Woche (je ca. 60min)',
      price_monthly: 75, price_full: 600, is_active: true, color: '',
      skills: ['Madina Buch 1 & Arabya Bayna Yadayk 1', 'Grundlegende Grammatik verstehen', 'Ersten Wortschatz sicher anwenden'],
    },
    {
      id: 'level-3', level: 'Level 3', title: 'Madina Buch 2 & Arabya Bayna Yadayk 2',
      description: 'Vertiefe dein Verständnis und baue Grammatik, Wortschatz und Lesekompetenz aus.',
      duration: 'ca. 34 Wochen', sessions: '2x die Woche (je ca. 60min)',
      price_monthly: 75, price_full: 600, is_active: true, color: '',
      skills: ['Komplexere Satzstrukturen', 'Längere Texte verstehen', 'Sicherer Umgang mit Grammatik'],
    },
    {
      id: 'level-4', level: 'Level 4', title: 'Madina Buch 3 & Arabya Bayna Yadayk 3',
      description: 'Für Lernende, die Arabisch sicher lesen und ihre Fähigkeiten vertiefen möchten.',
      duration: 'ca. 34 Wochen', sessions: '2x die Woche (je ca. 60min)',
      price_monthly: 75, price_full: 600, is_active: true, color: '',
      skills: ['Fortgeschrittene Grammatik', 'Vertieftes Textverständnis', 'Mehr Ausdruckssicherheit'],
    },
    {
      id: 'level-5', level: 'Level 5', title: 'Al-Ajrummiyah & Arabya Bayna Yadayk 4',
      description: 'Für fortgeschrittene Lernende mit Fokus auf klassische Grammatik.',
      duration: 'ca. 34 Wochen', sessions: '2x die Woche (je ca. 60min)',
      price_monthly: 75, price_full: 600, is_active: true, color: '',
      skills: ['Klassische Grammatik verstehen', 'Fortgeschrittene Texte lesen', 'Sprachgefühl ausbauen'],
    },
    {
      id: 'level-6', level: 'Level 6', title: 'Meisterstufe',
      description: 'Für alle, die arabische Werke selbstständig lesen und auf hohem Niveau arbeiten möchten.',
      duration: 'ca. 34 Wochen', sessions: '2x die Woche (je ca. 90min)',
      price_monthly: 75, price_full: 600, is_active: true, color: '',
      skills: ['Originaltexte verstehen', 'Großen Wortschatz anwenden', 'Freier Umgang mit Hocharabisch'],
    },
  ];

  // Im Admin gepflegte Kursdaten laden; bei Fehlern bleiben die eingebauten Inhalte
  const [courses, setCourses] = useState<CourseData[]>(fallbackCourses);

  useEffect(() => {
    let active = true;
    fetchWebsiteCourses().then(data => {
      if (!active || !data) return;
      setCourses(
        data.map(c => ({
          id: c.id,
          level: c.level,
          // Auf der Anmeldeseite ist der Buch-Untertitel der Kursname
          title: c.subtitle || c.title,
          description: c.description,
          duration: c.duration,
          // Hier zählt der Rhythmus ("2x die Woche"), nicht die Gesamtzahl
          sessions: c.frequency || c.sessions,
          price_monthly: Number(c.price_monthly),
          price_full: Number(c.price_full),
          is_active: c.is_active,
          max_participants: c.max_participants,
          color: '',
          skills: c.skills ?? [],
        }))
      );
    });
    return () => { active = false; };
  }, []);

  const [formData, setFormData] = useState<FormData>(() => {
    try {
      const saved = localStorage.getItem(FORM_STORAGE_KEY);
      if (!saved) return defaultFormData;

      const parsed = JSON.parse(saved) as Partial<FormData>;
      return { ...defaultFormData, ...parsed };
    } catch {
      try {
        localStorage.removeItem(FORM_STORAGE_KEY);
      } catch {
        // Ignore storage errors; the form can still use defaults.
      }
      return defaultFormData;
    }
  });

  useEffect(() => {
    const courseFromUrl = searchParams.get('course');
    const levelFromUrl = searchParams.get('level');
    if (courseFromUrl) {
      setFormData(prev => ({ ...prev, selectedCourse: courseFromUrl, currentLevel: levelFromUrl || prev.currentLevel }));
    }
  }, [searchParams]);

  useEffect(() => {
    try {
      localStorage.setItem(FORM_STORAGE_KEY, JSON.stringify(formData));
    } catch {
      // Ignore storage errors; submitting the current form state still works.
    }
  }, [formData]);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const allCourses = courses;
  const selectedCourse = allCourses.find(c => c.level === formData.selectedCourse);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value }));
    if (validationError) setValidationError('');
  };

  const validateStep = (step: number): string[] => {
    const missing: string[] = [];
    if (step === 1) {
      if (!formData.selectedCourse) missing.push('Kursauswahl');
      if (!formData.firstName) missing.push('Vorname');
      if (!formData.lastName) missing.push('Nachname');
      if (!formData.email) missing.push('E-Mail-Adresse');
      if (!formData.phone) missing.push('Telefonnummer');
      if (!formData.gender) missing.push('Geschlecht');
      if (!formData.age) missing.push('Alter');
      if (!formData.paymentMethod) missing.push('Zahlungsart');
    }
    if (step === 2) {
      if (!formData.privacy) missing.push('Datenschutzerklärung');
      if (!formData.terms) missing.push('Allgemeine Geschäftsbedingungen');
    }
    return missing;
  };

  const nextStep = () => {
    const missing = validateStep(currentStep);
    if (missing.length > 0) {
      setValidationError(`Bitte füllen Sie folgende Pflichtfelder aus: ${missing.join(', ')}`);
      setTimeout(() => setValidationError(''), 5000);
      return;
    }
    setCurrentStep(prev => prev + 1);
    setValidationError('');
    scrollToTop();
  };

  const prevStep = () => {
    if (currentStep > 1) { setCurrentStep(prev => prev - 1); setValidationError(''); scrollToTop(); }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const missing = validateStep(2);
    if (missing.length > 0) { setValidationError(`Bitte stimmen Sie folgenden Punkten zu: ${missing.join(', ')}`); return; }
    submitRegistration();
  };

  const submitRegistration = async () => {
    try {
      setIsSubmitting(true);
      if (!isSupabaseConfigured) {
        setValidationError(supabaseConfigError ?? 'Anmeldung ist aktuell nicht verfügbar.');
        setIsSubmitting(false);
        return;
      }

      const ageNumber = parseInt(formData.age, 10);
      if (isNaN(ageNumber) || ageNumber < 16 || ageNumber > 99) {
        setValidationError('Bitte geben Sie ein gültiges Alter zwischen 16 und 99 Jahren ein.');
        setIsSubmitting(false);
        return;
      }
      const registrationData: RegistrationData = {
        first_name: formData.firstName, last_name: formData.lastName,
        email: formData.email, phone: formData.phone, gender: formData.gender,
        age: ageNumber, instagram: formData.instagram || null,
        current_level: formData.currentLevel, selected_course: formData.selectedCourse,
        start_date: 'contact-me',
        payment_method: formData.paymentMethod, privacy_accepted: formData.privacy,
        terms_accepted: formData.terms, withdrawal_consent: formData.withdrawalConsent,
        newsletter_opt_in: formData.newsletter,
      };
      // Kein .select() nach dem Insert: Das würde ein RETURNING auslösen, und
      // Lesen dürfen auf registrations nur Admins (RLS) — der Insert selbst
      // ist für anonyme Besucher erlaubt.
      const { error } = await supabase.from('registrations').insert([registrationData]);
      if (error) {
        if ('code' in error && error.code === '23505') setValidationError('Diese E-Mail-Adresse ist bereits registriert.');
        else setValidationError(`Fehler: ${error.message}. Bitte versuchen Sie es erneut.`);
        return;
      }
      const { error: mailError } = await supabase.functions.invoke('send-registration-confirmation', {
        body: { firstName: formData.firstName, lastName: formData.lastName, email: formData.email, phone: formData.phone, gender: formData.gender, age: formData.age, instagram: formData.instagram, currentLevel: formData.currentLevel, selectedCourse: formData.selectedCourse, paymentMethod: formData.paymentMethod },
      });
      if (mailError) {
        if (import.meta.env.DEV) console.error('Registrierungs-Mail Fehler:', mailError);
        setValidationError('Anmeldung gespeichert, aber die Bestätigungs-E-Mail konnte nicht gesendet werden. Bitte prüfe deinen Spam-Ordner oder kontaktiere uns.');
      }
      setIsSubmitted(true);
      try { localStorage.removeItem(FORM_STORAGE_KEY); } catch { /* ignore storage errors */ }
      scrollToTop();
    } catch (error) {
      if (import.meta.env.DEV) console.error('Unerwarteter Fehler:', error);
      setValidationError('Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ── Success screen ── */
  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-white">
        <SEO title="Kursanfrage gesendet" description="Deine Kursanfrage wurde erfolgreich übermittelt." url="/anmelden" noIndex={true} />
        <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center h-16">
            <Link to="/"><img src="/LogoHeader4.png" alt="Arabicroots" className="h-9 object-contain" /></Link>
          </div>
        </header>
        <main className="max-w-2xl mx-auto px-4 py-20 text-center">
          <div className="w-16 h-16 bg-green-50 border border-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-3">Kursanfrage gesendet</h1>
          <p className="text-[16px] text-gray-500 mb-10 leading-relaxed">
            Vielen Dank! Wir haben deine Kursanfrage erhalten und melden uns so schnell wie möglich bei dir.
          </p>

          <div className="rounded-2xl border border-gray-100 bg-gray-50/60 p-6 mb-6 text-left">
            <h3 className="text-[12px] font-semibold text-gray-900 mb-4 uppercase tracking-widest">Deine Kursanfrage</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { label: 'Name', value: `${formData.firstName} ${formData.lastName}` },
                { label: 'E-Mail', value: formData.email },
                { label: 'Telefon', value: formData.phone },
                { label: 'Kurs', value: selectedCourse?.title ?? '' },
              ].map(({ label, value }) => (
                <div key={label} className="rounded-xl bg-white border border-gray-100 p-3.5">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[#970000] mb-1">{label}</p>
                  <p className="text-[13px] text-gray-700">{value}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="relative overflow-hidden rounded-2xl bg-[#970000] p-6 text-white mb-8 text-left">
            <div className="pointer-events-none absolute inset-0 [background-image:linear-gradient(to_right,rgba(255,255,255,0.06)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.06)_1px,transparent_1px)] [background-size:24px_24px]" />
            <div className="relative">
              <h3 className="text-[12px] font-semibold mb-4 uppercase tracking-widest">Wie es weitergeht</h3>
              <div className="space-y-3">
                {['Wir prüfen deine Anfrage und bestätigen dir deinen Kursplatz per E-Mail oder WhatsApp.', 'Sobald deine Gruppe vollständig ist – erfahrungsgemäß nach ca. 3–4 Wochen – steht der Starttermin fest.', 'Du erhältst dann alle Details, den Zugang zum Schülerbereich und es geht los.'].map((text, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-5 h-5 bg-white text-[#970000] rounded-full flex items-center justify-center text-xs font-bold shrink-0">{i + 1}</div>
                    <span className="text-[13px] text-white/80">{text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/" className="inline-flex items-center justify-center rounded-full bg-[#970000] px-6 py-3 font-semibold text-[14px] text-white hover:bg-[#7a0000] transition-colors">
              Zur Startseite
            </Link>
            <a href="mailto:info@arabicroots.de" className="inline-flex items-center justify-center rounded-full border border-gray-200 px-6 py-3 font-semibold text-[14px] text-gray-700 hover:bg-gray-50 transition-colors">
              Fragen? E-Mail senden
            </a>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  /* ── Main form ── */
  return (
    <div className="min-h-screen bg-[#fafafa]">
      <SEO title="Kursanmeldung" description="Melden Sie sich jetzt für unsere Live-Online-Arabischkurse an." url="/anmelden" />

      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/"><img src="/LogoHeader4.png" alt="Arabicroots" className="h-9 object-contain" /></Link>
          <Link to="/?scroll=kurse" className="flex items-center gap-1.5 text-[13px] text-gray-500 hover:text-gray-900 transition-colors">
            <ArrowLeft className="h-4 w-4" />
            Zurück zu den Kursen
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">

        {/* Page title */}
        <div className="mb-8 md:mb-10">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#970000] mb-2">
            Schritt {currentStep} von {totalSteps}
          </p>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 tracking-[-0.03em]">
            {currentStep === 1 ? 'Deine Angaben' : 'Bestätigung & Abschluss'}
          </h1>
          <p className="mt-2 text-[15px] text-gray-500">
            {currentStep === 1
              ? 'Wähle deinen Kurs und trage deine persönlichen Daten ein.'
              : 'Prüfe deine Angaben und schließe die Anmeldung ab.'}
          </p>
        </div>

        {/* Step progress */}
        <div className="flex items-center gap-3 mb-10">
          {[1, 2].map(step => (
            <React.Fragment key={step}>
              <div className="flex items-center gap-2.5">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[12px] font-bold border-2 transition-all duration-300 ${
                  step < currentStep ? 'bg-[#970000] border-[#970000] text-white' :
                  step === currentStep ? 'border-[#970000] text-[#970000] bg-white' :
                  'border-gray-200 text-gray-400 bg-white'
                }`}>
                  {step < currentStep ? <CheckCircle className="h-4 w-4" /> : step}
                </div>
                <span className={`text-[13px] font-semibold hidden sm:inline transition-colors ${step <= currentStep ? 'text-gray-800' : 'text-gray-400'}`}>
                  {step === 1 ? 'Deine Angaben' : 'Bestätigung'}
                </span>
              </div>
              {step < totalSteps && (
                <div className="flex-1 max-w-[60px] h-px bg-gray-200 relative overflow-hidden">
                  <div className={`absolute inset-y-0 left-0 bg-[#970000] transition-all duration-500 ${step < currentStep ? 'w-full' : 'w-0'}`} />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Validation error */}
        {validationError && (
          <div className="mb-8 rounded-xl border border-red-100 bg-red-50 p-4 flex items-start gap-3">
            <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
            <p className="text-sm text-red-600">{validationError}</p>
          </div>
        )}

        <form onSubmit={handleSubmit}>

          {/* ── STEP 1 ── */}
          {currentStep === 1 && (
            <div className="grid lg:grid-cols-[1fr_320px] gap-8 items-start">

              {/* Left: form sections */}
              <div className="space-y-6">

                {/* Kursauswahl */}
                <section className="bg-white rounded-2xl border border-gray-100 shadow-[0_2px_12px_rgba(0,0,0,0.04)] overflow-hidden">
                  <div className="flex items-center gap-3 px-4 sm:px-6 py-4 border-b border-gray-100 bg-gray-50/50">
                    <div className="w-8 h-8 rounded-lg bg-[#970000]/[0.08] flex items-center justify-center text-[#970000]">
                      <BookOpen className="h-4 w-4" />
                    </div>
                    <h2 className="text-[15px] font-bold text-gray-900">Kursauswahl</h2>
                  </div>
                  <div className="px-4 sm:px-6 py-5 space-y-4">
                    <div>
                      <label htmlFor="selectedCourse" className={labelClass}>Kurs *</label>
                      <div className="relative">
                        <select
                          id="selectedCourse" name="selectedCourse"
                          value={formData.selectedCourse} onChange={handleChange}
                          disabled={lockedCourseFromUrl}
                          className={`${inputClass} appearance-none pr-10 ${lockedCourseFromUrl ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''}`}
                          required
                        >
                          <option value="">Bitte Kurs wählen</option>
                          {allCourses.map(c => (
                            <option key={c.level} value={c.level}>{c.level} – {c.title}</option>
                          ))}
                        </select>
                        <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      </div>
                    </div>

                    {selectedCourse && (
                      <div className="rounded-xl border border-[#970000]/15 bg-[#970000]/[0.03] p-4 flex items-start gap-3">
                        <div className="w-9 h-9 rounded-lg bg-[#970000]/[0.08] flex items-center justify-center text-[#970000] shrink-0 mt-0.5">
                          <BookOpen className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="text-[11px] text-gray-400 uppercase tracking-wider mb-0.5">{selectedCourse.level}</p>
                          <p className="text-[15px] font-semibold text-gray-900 leading-snug">{selectedCourse.title}</p>
                          <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                            <span className="flex items-center gap-1 text-[12px] text-gray-500">
                              <Clock className="h-3 w-3" />{selectedCourse.duration}
                            </span>
                            <span className="text-gray-300 hidden sm:inline">·</span>
                            <span className="text-[12px] text-gray-500">{selectedCourse.sessions}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </section>

                {/* Persönliche Daten */}
                <section className="bg-white rounded-2xl border border-gray-100 shadow-[0_2px_12px_rgba(0,0,0,0.04)] overflow-hidden">
                  <div className="flex items-center gap-3 px-4 sm:px-6 py-4 border-b border-gray-100 bg-gray-50/50">
                    <div className="w-8 h-8 rounded-lg bg-[#970000]/[0.08] flex items-center justify-center text-[#970000]">
                      <User className="h-4 w-4" />
                    </div>
                    <h2 className="text-[15px] font-bold text-gray-900">Persönliche Daten</h2>
                  </div>
                  <div className="px-4 sm:px-6 py-5 space-y-5">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="firstName" className={labelClass}>Vorname *</label>
                        <input type="text" id="firstName" name="firstName" value={formData.firstName} onChange={handleChange} className={inputClass} placeholder="Dein Vorname" required />
                      </div>
                      <div>
                        <label htmlFor="lastName" className={labelClass}>Nachname *</label>
                        <input type="text" id="lastName" name="lastName" value={formData.lastName} onChange={handleChange} className={inputClass} placeholder="Dein Nachname" required />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="email" className={labelClass}>E-Mail-Adresse *</label>
                      <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} className={inputClass} placeholder="deine@email.de" required />
                    </div>

                    <div>
                      <label htmlFor="phone" className={labelClass}>WhatsApp-Nummer *</label>
                      <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} className={inputClass} placeholder="+49 123 456789" required />
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="gender" className={labelClass}>Geschlecht *</label>
                        <div className="relative">
                          <select id="gender" name="gender" value={formData.gender} onChange={handleChange} className={`${inputClass} appearance-none pr-10`} required>
                            <option value="">Bitte wählen</option>
                            <option value="male">Männlich</option>
                            <option value="female">Weiblich</option>
                          </select>
                          <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="age" className={labelClass}>Alter *</label>
                        <input type="number" id="age" name="age" value={formData.age} onChange={handleChange} className={inputClass} placeholder="z. B. 25" min="16" max="99" step="1" required />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="instagram" className={labelClass}>
                        Instagram <span className="text-gray-400 font-normal normal-case">(optional)</span>
                      </label>
                      <input type="text" id="instagram" name="instagram" value={formData.instagram} onChange={handleChange} className={inputClass} placeholder="@deinbenutzername" />
                    </div>
                  </div>
                </section>

                {/* Zahlungsart */}
                <section className="bg-white rounded-2xl border border-gray-100 shadow-[0_2px_12px_rgba(0,0,0,0.04)] overflow-hidden">
                  <div className="flex items-center gap-3 px-4 sm:px-6 py-4 border-b border-gray-100 bg-gray-50/50">
                    <div className="w-8 h-8 rounded-lg bg-[#970000]/[0.08] flex items-center justify-center text-[#970000]">
                      <CreditCard className="h-4 w-4" />
                    </div>
                    <h2 className="text-[15px] font-bold text-gray-900">Zahlungsart *</h2>
                  </div>
                  <div className="px-4 sm:px-6 py-5 space-y-3">
                    {[
                      { value: 'monthly', label: 'Monatliche Ratenzahlung', sub: 'Kursgebühr in monatlichen Raten über den gesamten Kursabschnitt', price: selectedCourse?.price_monthly ? `€${selectedCourse.price_monthly}/Monat` : '–' },
                      { value: 'full', label: 'Einmalzahlung', sub: 'Kompletter Kursbetrag in einer Zahlung', price: selectedCourse?.price_full ? `€${selectedCourse.price_full}` : '–' },
                    ].map(opt => (
                      <div
                        key={opt.value}
                        onClick={() => setFormData(prev => ({ ...prev, paymentMethod: opt.value }))}
                        className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all duration-150 ${
                          formData.paymentMethod === opt.value
                            ? 'border-[#970000] bg-[#970000]/[0.03]'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50/50'
                        }`}
                      >
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 transition-all ${
                          formData.paymentMethod === opt.value ? 'border-[#970000]' : 'border-gray-300'
                        }`}>
                          {formData.paymentMethod === opt.value && <div className="w-2 h-2 rounded-full bg-[#970000]" />}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <span className="text-[14px] font-semibold text-gray-900">{opt.label}</span>
                            <span className="text-[15px] font-bold text-[#970000]">{opt.price}</span>
                          </div>
                          <p className="text-[12px] text-gray-500 mt-0.5">{opt.sub}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

              </div>

              {/* Right: sticky sidebar */}
              <div className="hidden lg:block sticky top-24 space-y-4">
                <div className="bg-white rounded-2xl border border-gray-100 shadow-[0_2px_12px_rgba(0,0,0,0.04)] p-5">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-gray-400 mb-4">Deine Auswahl</p>
                  {selectedCourse ? (
                    <div className="space-y-3">
                      <div>
                        <p className="text-[11px] text-gray-400 uppercase tracking-wider">{selectedCourse.level}</p>
                        <p className="text-[15px] font-bold text-gray-900 mt-0.5 leading-snug">{selectedCourse.title}</p>
                      </div>
                      <div className="h-px bg-gray-100" />
                      <div className="space-y-1.5 text-[13px] text-gray-500">
                        <div className="flex items-center gap-2">
                          <Clock className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                          {selectedCourse.duration}
                        </div>
                        <div className="flex items-center gap-2">
                          <BookOpen className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                          {selectedCourse.sessions}
                        </div>
                      </div>
                      {formData.paymentMethod && (
                        <>
                          <div className="h-px bg-gray-100" />
                          <div className="flex items-center justify-between">
                            <span className="text-[13px] text-gray-500">
                              {formData.paymentMethod === 'monthly' ? 'Monatliche Ratenzahlung' : 'Einmalzahlung'}
                            </span>
                            <span className="text-[16px] font-bold text-[#970000]">
                              {formData.paymentMethod === 'monthly'
                                ? `€${selectedCourse.price_monthly}/Mo.`
                                : `€${selectedCourse.price_full}`}
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  ) : (
                    <p className="text-[13px] text-gray-400 italic">Noch kein Kurs gewählt</p>
                  )}
                </div>

                <div className="rounded-2xl border border-gray-100 bg-gray-50/70 p-4 flex items-start gap-3">
                  <Shield className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
                  <p className="text-[12px] text-gray-500 leading-relaxed">
                    Nach deiner Anmeldung melden wir uns bei dir und besprechen alle weiteren Details gemeinsam.
                  </p>
                </div>
              </div>

            </div>
          )}

          {/* ── STEP 2 ── */}
          {currentStep === 2 && (
            <div className="grid lg:grid-cols-[1fr_320px] gap-8 items-start">
              <div className="space-y-6">

                {/* Summary */}
                <section className="bg-white rounded-2xl border border-gray-100 shadow-[0_2px_12px_rgba(0,0,0,0.04)] overflow-hidden">
                  <div className="flex items-center gap-3 px-4 sm:px-6 py-4 border-b border-gray-100 bg-gray-50/50">
                    <div className="w-8 h-8 rounded-lg bg-[#970000]/[0.08] flex items-center justify-center text-[#970000]">
                      <Award className="h-4 w-4" />
                    </div>
                    <h2 className="text-[15px] font-bold text-gray-900">Zusammenfassung</h2>
                  </div>
                  <div className="px-4 sm:px-6 py-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#970000] mb-2.5">Persönliche Daten</p>
                        <div className="space-y-1">
                          <p className="text-[14px] font-semibold text-gray-900">{formData.firstName} {formData.lastName}</p>
                          <p className="text-[13px] text-gray-500">{formData.email}</p>
                          <p className="text-[13px] text-gray-500">{formData.phone}</p>
                          <p className="text-[13px] text-gray-500">{formData.gender === 'male' ? 'Männlich' : 'Weiblich'}, {formData.age} Jahre</p>
                          {formData.instagram && <p className="text-[13px] text-gray-500">{formData.instagram}</p>}
                        </div>
                      </div>
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#970000] mb-2.5">Kurs & Zahlung</p>
                        <div className="space-y-1">
                          <p className="text-[14px] font-semibold text-gray-900">{selectedCourse?.title}</p>
                          <p className="text-[13px] text-gray-500">
                            {formData.paymentMethod === 'monthly'
                              ? `Monatliche Ratenzahlung · €${selectedCourse?.price_monthly}/Monat`
                              : `Einmalzahlung · €${selectedCourse?.price_full}`}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="mt-5 pt-4 border-t border-gray-100">
                      <button
                        type="button"
                        onClick={() => { setCurrentStep(1); scrollToTop(); }}
                        className="text-[13px] text-[#970000] font-semibold hover:underline"
                      >
                        Angaben bearbeiten
                      </button>
                    </div>
                  </div>
                </section>

                {/* Zustimmung */}
                <section className="bg-white rounded-2xl border border-gray-100 shadow-[0_2px_12px_rgba(0,0,0,0.04)] overflow-hidden">
                  <div className="flex items-center gap-3 px-4 sm:px-6 py-4 border-b border-gray-100 bg-gray-50/50">
                    <div className="w-8 h-8 rounded-lg bg-[#970000]/[0.08] flex items-center justify-center text-[#970000]">
                      <Shield className="h-4 w-4" />
                    </div>
                    <h2 className="text-[15px] font-bold text-gray-900">Zustimmung</h2>
                  </div>
                  <div className="px-4 sm:px-6 py-5 space-y-4">
                    {[
                      { id: 'privacy', name: 'privacy', checked: formData.privacy, label: <span>Ich stimme der <Link to="/datenschutz" target="_blank" rel="noopener noreferrer" className="text-[#970000] hover:underline">Datenschutzerklärung</Link> zu. *</span> },
                      { id: 'terms', name: 'terms', checked: formData.terms, label: <span>Ich akzeptiere die <Link to="/agb" target="_blank" rel="noopener noreferrer" className="text-[#970000] hover:underline">Allgemeinen Geschäftsbedingungen</Link>. *</span> },
                      { id: 'withdrawalConsent', name: 'withdrawalConsent', checked: formData.withdrawalConsent, label: <span>Ich verlange ausdrücklich, dass mit der Kursleistung und der Bereitstellung digitaler Inhalte (z.&nbsp;B. Aufzeichnungen, Materialien) bereits vor Ablauf der Widerrufsfrist begonnen wird. Mir ist bekannt, dass mein Widerrufsrecht mit vollständiger Vertragserfüllung bzw. mit Beginn der Bereitstellung digitaler Inhalte erlischt. <span className="text-gray-400">(optional – ohne diese Zustimmung erhältst du den Zugang erst nach Ablauf der 14-tägigen Widerrufsfrist)</span></span> },
                      { id: 'newsletter', name: 'newsletter', checked: formData.newsletter, label: <span>Ich möchte über neue Kurse informiert werden. <span className="text-gray-400">(optional)</span></span> },
                    ].map(item => (
                      <label key={item.id} className="flex items-start gap-3.5 cursor-pointer group">
                        <div className={`mt-0.5 w-[18px] h-[18px] rounded-md border-2 flex items-center justify-center shrink-0 transition-all ${item.checked ? 'bg-[#970000] border-[#970000]' : 'border-gray-300 group-hover:border-[#970000]/50'}`}>
                          {item.checked && <CheckCircle className="h-3 w-3 text-white" />}
                        </div>
                        <input type="checkbox" id={item.id} name={item.name} checked={item.checked} onChange={handleChange} className="sr-only" />
                        <span className="text-[13px] text-gray-600 leading-relaxed">{item.label}</span>
                      </label>
                    ))}
                  </div>
                </section>

              </div>

              {/* Right sidebar step 2 */}
              <div className="hidden lg:block sticky top-24 space-y-4">
                <div className="bg-white rounded-2xl border border-gray-100 shadow-[0_2px_12px_rgba(0,0,0,0.04)] p-5">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-gray-400 mb-4">Deine Kursanfrage</p>
                  <div className="space-y-3">
                    <div>
                      <p className="text-[11px] text-gray-400 uppercase tracking-wider">{selectedCourse?.level}</p>
                      <p className="text-[15px] font-bold text-gray-900 mt-0.5 leading-snug">{selectedCourse?.title}</p>
                    </div>
                    <div className="h-px bg-gray-100" />
                    <div className="flex items-center justify-between">
                      <span className="text-[13px] text-gray-500">
                        {formData.paymentMethod === 'monthly' ? 'Monatliche Ratenzahlung' : 'Einmalzahlung'}
                      </span>
                      <span className="text-[16px] font-bold text-[#970000]">
                        {formData.paymentMethod === 'monthly'
                          ? `€${selectedCourse?.price_monthly}/Mo.`
                          : `€${selectedCourse?.price_full}`}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="relative overflow-hidden rounded-2xl bg-[#970000] p-5 text-white">
                  <div className="pointer-events-none absolute inset-0 [background-image:linear-gradient(to_right,rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.05)_1px,transparent_1px)] [background-size:20px_20px]" />
                  <div className="relative">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-white/60 mb-3">Wie es weitergeht</p>
                    <div className="space-y-2.5">
                      {['Anmeldung prüfen', 'Details per Kontakt', 'Kursstart vorbereiten'].map((text, i) => (
                        <div key={i} className="flex items-center gap-2.5">
                          <div className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0">{i + 1}</div>
                          <span className="text-[12px] text-white/80">{text}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between mt-10">
            <button
              type="button"
              onClick={prevStep}
              disabled={currentStep === 1 || isSubmitting}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[14px] font-semibold transition-all ${
                currentStep === 1
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-white hover:shadow-sm border border-transparent hover:border-gray-200'
              }`}
            >
              <ArrowLeft className="h-4 w-4" />
              Zurück
            </button>

            {currentStep < totalSteps ? (
              <button
                type="button"
                onClick={nextStep}
                className="inline-flex items-center gap-2 rounded-full bg-[#970000] px-7 py-2.5 text-[14px] font-semibold text-white hover:bg-[#7a0000] transition-colors shadow-[0_1px_6px_rgba(151,0,0,0.3)]"
              >
                Weiter
                <ArrowRight className="h-4 w-4" />
              </button>
            ) : (
              <button
                type="submit"
                disabled={!formData.privacy || !formData.terms || isSubmitting}
                className="inline-flex items-center gap-2 rounded-full bg-[#970000] px-7 py-2.5 text-[14px] font-semibold text-white hover:bg-[#7a0000] transition-colors shadow-[0_1px_6px_rgba(151,0,0,0.3)] disabled:bg-gray-200 disabled:text-gray-400 disabled:shadow-none disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <><div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />Wird gesendet...</>
                ) : (
                  <>Kursanfrage absenden <CheckCircle className="h-4 w-4" /></>
                )}
              </button>
            )}
          </div>

          {currentStep === totalSteps && (
            <p className="mt-4 text-center text-[12px] text-gray-400 leading-relaxed">
              Mit dem Absenden stellst du eine Kursanfrage. Ein Vertrag kommt erst zustande,
              wenn wir deine Anmeldung per E-Mail oder WhatsApp bestätigen (siehe AGB, Ziffer 2).
            </p>
          )}

        </form>
      </main>

      <Footer />
    </div>
  );
};

export default RegistrationPage;
